import { FC } from 'react';
import { useState } from 'react';
import { Card } from '../ui/card';
import { ParentCommunityFeed } from './ParentCommunityFeed';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Badge } from '../ui/badge';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { AlertTriangle, CheckCircle, XCircle } from 'lucide-react';

interface ContentReport {
  id: string;
  content_type: string;
  reported_content: string;
  reason: string;
  status: string;
  severity: string;
  created_at: string;
  resolved_at: string | null;
  action_taken: string | null;
  notes: string | null;
  reporter_name: string | null;
  reported_user_name: string | null;
  reporter_email: string | null;
  reported_user_email: string | null;
  tutor_viewed_at: string | null;
  tutor_notified: boolean | null;
  is_admin_action?: boolean;
  post_id?: string;
}

export const CommunityTab: FC = () => {
  const [selectedTab, setSelectedTab] = useState('activity');
  const { user } = useAuth();

  const { data: moderationHistory, isLoading: historyLoading } = useQuery({
    queryKey: ['moderationHistory', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      // Obtener reportes
      const { data: reports, error: reportsError } = await supabase.rpc('get_user_related_reports', {
        user_id: user.id
      });

      if (reportsError) throw reportsError;

      // Obtener moderaciones administrativas
      const { data: adminModerations, error: moderationsError } = await supabase
        .from('moderation_logs')
        .select(`
          id,
          action,
          reason,
          content,
          content_type,
          post_id,
          created_at
        `)
        .eq('user_id', user.id);

      if (moderationsError) throw moderationsError;

      // Formatear reportes
      const formattedReports = (reports || []).map((report: any) => ({
        id: report.id,
        content_type: 'post',
        reported_content: report.reported_content,
        reason: report.reason,
        status: report.status,
        severity: report.severity,
        created_at: report.created_at,
        resolved_at: report.resolved_at,
        action_taken: report.action_taken,
        notes: report.notes,
        reporter_name: report.reporter_name,
        reported_user_name: report.reported_user_name,
        reporter_email: report.reporter_email,
        reported_user_email: report.reported_user_email,
        tutor_viewed_at: report.tutor_viewed_at,
        tutor_notified: report.tutor_notified,
        is_admin_action: false
      }));

      // Formatear moderaciones administrativas
      const formattedModerations = (adminModerations || []).map(mod => ({
        id: mod.id,
        content_type: mod.content_type || 'post',
        post_id: mod.post_id,
        reported_content: mod.content,
        reason: mod.reason,
        status: 'resolved',
        severity: 'high',
        created_at: mod.created_at,
        resolved_at: mod.created_at,
        action_taken: mod.action === 'delete' ? 'Contenido eliminado' : 'Contenido archivado',
        notes: null,
        reporter_name: null,
        reported_user_name: null,
        reporter_email: null,
        reported_user_email: null,
        tutor_viewed_at: null,
        tutor_notified: null,
        is_admin_action: true
      }));

      // Combinar y ordenar por fecha
      return [...formattedReports, ...formattedModerations]
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    },
    enabled: !!user?.id,
  });

  const renderReportStatus = (status: string) => {
    switch (status.toLowerCase()) {
      case 'resolved':
        return (
          <Badge variant="outline" className="flex items-center gap-1 bg-blue-50 text-blue-700 border-blue-200">
            <CheckCircle className="w-3 h-3" />
            Resuelto
          </Badge>
        );
      case 'dismissed':
        return (
          <Badge variant="outline" className="flex items-center gap-1 bg-red-50 text-red-700 border-red-200">
            <XCircle className="w-3 h-3" />
            Descartado
          </Badge>
        );
      case 'pending':
        return (
          <Badge variant="outline" className="flex items-center gap-1 bg-yellow-50 text-yellow-700 border-yellow-200">
            <AlertTriangle className="w-3 h-3" />
            Pendiente
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            {status}
          </Badge>
        );
    }
  };

  const translateReason = (reason: string) => {
    const reasonTranslations: { [key: string]: string } = {
      'spam': 'Contenido no solicitado',
      'inappropriate': 'Contenido inapropiado',
      'harassment': 'Acoso o abuso',
      'misinformation': 'Información falsa o engañosa',
      'other': 'Otro motivo',
      'Violación de las normas de la comunidad': 'Violación de las normas de la comunidad'
    };
    return reasonTranslations[reason.toLowerCase()] || reason;
  };

  const translateAction = (action: string) => {
    const actionTranslations: { [key: string]: string } = {
      'Content reviewed and action taken': 'Contenido revisado y acción tomada',
      'Content hidden': 'Contenido ocultado',
      'Content deleted': 'Contenido eliminado',
      'Warning issued': 'Advertencia emitida',
      'No action needed': 'No se requiere acción',
      'Contenido eliminado': 'Contenido eliminado',
      'Contenido archivado': 'Contenido archivado'
    };
    return actionTranslations[action] || action;
  };

  const renderReportsList = () => {
    if (historyLoading) {
      return (
        <div className="flex justify-center items-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      );
    }

    if (!moderationHistory?.length) {
      return (
        <div className="text-center py-8">
          <AlertTriangle className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">No hay historial de moderación para mostrar</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {moderationHistory.map((report) => (
          <Card key={report.id} className="p-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {report.is_admin_action ? (
                    <Badge variant="destructive" className="flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Acción Administrativa
                    </Badge>
                  ) : (
                    renderReportStatus(report.status)
                  )}
                  <span className="text-sm text-gray-500">
                    {format(new Date(report.created_at), "d 'de' MMMM 'a las' HH:mm", { locale: es })}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="capitalize">
                    {report.content_type === 'comment' ? 'Comentario' : 'Publicación'}
                  </Badge>
                </div>
              </div>

              <div className="space-y-3">
                <div className="bg-gray-50 p-3 rounded-md">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-700">
                        {report.is_admin_action ? 'Contenido moderado:' : 'Contenido reportado:'}
                      </p>
                      <span className="text-xs text-gray-500">
                        Razón: {translateReason(report.reason)}
                      </span>
                    </div>
                    <p className="text-sm">{report.reported_content}</p>
                  </div>
                </div>

                {(report.action_taken || report.is_admin_action) && (
                  <div className="bg-primary/5 p-3 rounded-md">
                    <div className="space-y-2">
                      <p className="text-sm">
                        <span className="font-medium">
                          {report.is_admin_action ? 'Acción administrativa:' : 'Acción tomada:'}
                        </span>{' '}
                        {translateAction(report.action_taken || '')}
                      </p>
                      {report.resolved_at && !report.is_admin_action && (
                        <p className="text-xs text-gray-500">
                          Resuelto el {format(new Date(report.resolved_at), "d 'de' MMMM 'a las' HH:mm", { locale: es })}
                        </p>
                      )}
                      {report.notes && (
                        <p className="text-sm mt-1">
                          <span className="font-medium">Notas:</span>{' '}
                          {report.notes}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-3 md:space-y-4">
      <Tabs value={selectedTab} onValueChange={setSelectedTab}>
        <TabsList className="w-full md:w-auto">
          <TabsTrigger value="activity" className="flex-1 md:flex-none">Actividad</TabsTrigger>
          <TabsTrigger value="moderation" className="flex-1 md:flex-none">Moderación</TabsTrigger>
        </TabsList>

        <TabsContent value="activity">
          <Card className="p-2 md:p-4">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2 mb-3 md:mb-4">
              <h2 className="text-base md:text-lg font-semibold">Actividad de tu Hijo/a</h2>
              <Button asChild variant="outline" size="sm" className="w-full md:w-auto">
                <Link to="/community">Ver Toda la Comunidad</Link>
              </Button>
            </div>
            <div className="max-w-[calc(100vw-2rem)] md:max-w-none overflow-hidden">
              <ParentCommunityFeed />
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="moderation">
          <Card className="p-2 md:p-4">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2 mb-3 md:mb-4">
              <div>
                <h2 className="text-base md:text-lg font-semibold">Historial de Moderación</h2>
                <p className="text-sm text-gray-500">
                  Aquí puedes ver el historial de reportes relacionados con tu actividad en la comunidad
                </p>
              </div>
            </div>
            {renderReportsList()}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
